/**
 * Agent-authored markdown, rendered — compactly, and safely.
 *
 * Employees structure their questions, digests and reports as markdown
 * (headings, numbered decision lists, bold). Rendering it beats the old
 * whitespace-only `<pre>` because a six-decision blocking question is read
 * under time pressure — structure has to be visible, not inferred.
 *
 * Safety: react-markdown renders to React elements and does NOT emit raw
 * HTML from the source (no rehype-raw here, ever) — so agent output, which
 * can quote text from external pages and emails, never reaches
 * `dangerouslySetInnerHTML`. Links open in a new tab with `rel="noreferrer"`;
 * `file://` links go through `markdownUrlTransform` (→ Jarvis Helper).
 * Keep it that way: adding rehype-raw would turn every quoted email into an
 * injection surface.
 *
 * IDs the employees quote ("question d2de109f") become links once the lookup
 * resolves them (`lib/hooks/useRemarkPlugins.ts`); tokens that match
 * nothing – commit hashes, numbers – stay plain text.
 *
 * Styling lives in `index.css` under `.md-block` — GitHub-flavored but
 * tighter, matching the admin surface's developer-console look.
 */
import ReactMarkdown from "react-markdown";

import { useRemarkPlugins } from "../lib/hooks/useRemarkPlugins";
import { markdownUrlTransform } from "../lib/markdownUrls";
import { LinkAnchor } from "./LinkAnchor";

export function Markdown({ text }: { text?: string | null }) {
  const remarkPlugins = useRemarkPlugins(text);
  if (!text) return <span className="text-sm text-ink-low">—</span>;
  return (
    <div className="md-block">
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        urlTransform={markdownUrlTransform}
        components={{
          a: ({ href, children }) => <LinkAnchor href={href}>{children}</LinkAnchor>,
          table: ({ children }) => (
            <div className="md-table-wrap">
              <table>{children}</table>
            </div>
          ),
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}
